import React, { useState } from 'react'
import { FaLock, FaSpinner, FaUserPlus } from 'react-icons/fa'
import { useAuth } from '../context/AuthContext'
import { Banner } from '../components/ui'
import { SHOW_STOCK } from '../lib/features'

/**
 * Sign in, register, and the first-run ownership claim — three states of one
 * screen.
 *
 * Registering is offered openly, which looks surprising for an admin tool but is
 * not: creating a login grants nothing at all. Access comes from an invitation
 * the owner recorded against that e-mail address, and every RLS policy keys off
 * admin_profiles. A stranger who registers reaches the "not authorised" panel and
 * can see nothing.
 *
 * Doing it this way means the owner never has to hand out the Supabase dashboard
 * and the panel never needs the service-role key — which cannot safely live in a
 * browser app.
 *
 * The claim path exists because a brand-new database has no administrators and
 * RLS forbids inserting the first one. claim_first_admin() refuses once any admin
 * exists, so it cannot be used later to take over a live store.
 */
const LoginPage = () => {
  const {
    signIn, signUp, claimOwnership, isSignedIn, profile, authError, signOut,
    invitedEmail, inviteForSomeoneElse, dismissInvite, sessionEmail, isSuspended,
  } = useAuth()

  // Arriving from an invitation link (/?invite=their@address): open on Register
  // with the invited address filled in, so the one thing that must match does.
  const [mode, setMode] = useState(invitedEmail ? 'signup' : 'signin') // 'signin' | 'signup'
  const [email, setEmail] = useState(invitedEmail)
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  // Signed in, but not on the allow-list. Distinct from "wrong password", and
  // worth saying plainly — otherwise the user stares at an empty dashboard and
  // assumes the app is broken.
  const signedInButUnauthorised = isSignedIn && !profile

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (mode === 'signup') {
        await signUp(email.trim(), password, fullName.trim() || null)
        // Depending on the project's e-mail-confirmation setting, the user may
        // or may not already have a session. Saying so is better than appearing
        // to do nothing.
        setNotice(
          'Account created. If you are not taken straight in, check your e-mail for a confirmation link, then sign in.',
        )
        setMode('signin')
      } else {
        await signIn(email.trim(), password)
      }
    } catch (err) {
      // The shop and this panel share one set of logins, so an invited person
      // who has ever ordered online already has an account. Registering again
      // fails; signing in with that account redeems the invitation.
      if (mode === 'signup' && /already registered/i.test(err.message)) {
        setMode('signin')
        setPassword('')
        setNotice('You already have a Coquis account with this address. Sign in with its password to accept the invitation.')
      } else {
        setError(err.message)
      }
    } finally {
      setBusy(false)
    }
  }

  const claim = async () => {
    setBusy(true)
    setError(null)
    try {
      await claimOwnership(fullName.trim() || null)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-navy px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="mb-3 inline-grid h-12 w-12 place-items-center rounded-xl bg-white/10 font-heading text-xl font-bold text-white">
            C
          </span>
          <h1 className="font-heading text-lg font-bold text-white">Coquis Store Manager</h1>
          <p className="mt-1 text-2xs text-white/55">
            {SHOW_STOCK ? 'Catalogue, stock and orders' : 'Catalogue, orders and invoices'}
          </p>
        </div>

        <div className="rounded-xl bg-surface p-5 shadow-pop">
          {inviteForSomeoneElse ? (
            <div className="space-y-4">
              <Banner tone="warning">
                This invitation is for <strong>{invitedEmail}</strong>, but this browser is signed
                in as <strong>{sessionEmail}</strong>.
              </Banner>
              <p className="text-2xs leading-relaxed text-ink-secondary">
                To accept it, sign out first. On a shared computer, sign out so the invited person
                can create their own account.
              </p>
              <button
                onClick={async () => {
                  await signOut()
                  setMode('signup')
                  setEmail(invitedEmail)
                }}
                className="btn-primary w-full"
              >
                <FaUserPlus />
                Sign out and accept the invitation
              </button>
              <button onClick={dismissInvite} className="btn-subtle w-full text-2xs">
                Stay signed in as {sessionEmail}
              </button>
            </div>
          ) : isSuspended ? (
            <div className="space-y-4">
              <Banner tone="warning">Your access to the store manager has been suspended.</Banner>
              <p className="text-2xs leading-relaxed text-ink-secondary">
                Ask the shop owner or a manager to restore it from Team.
              </p>
              <button onClick={signOut} className="btn-subtle w-full text-2xs">
                Sign in as someone else
              </button>
            </div>
          ) : signedInButUnauthorised ? (
            <div className="space-y-4">
              <Banner tone="warning">
                You are signed in, but this account does not have access yet.
              </Banner>

              <p className="text-2xs leading-relaxed text-ink-secondary">
                If the shop owner or a manager has invited you, they need to have used{' '}
                <strong>this exact e-mail address</strong> — ask them to check it and invite you
                again if not.
              </p>

              <div className="border-t border-hairline pt-4">
                <p className="mb-3 text-2xs leading-relaxed text-ink-secondary">
                  Setting up a brand-new store? Claim the owner account. This only works while no
                  administrator exists at all — after that, the owner or a manager has to invite you.
                </p>

                <label className="label" htmlFor="fullName">
                  Your name
                </label>
                <input
                  id="fullName"
                  className="input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Dupre Myer"
                  autoComplete="name"
                />

                {error && (
                  <div className="mt-3">
                    <Banner tone="error">{error}</Banner>
                  </div>
                )}

                <button onClick={claim} disabled={busy} className="btn-primary mt-3 w-full">
                  {busy ? <FaSpinner className="animate-spin" /> : <FaLock />}
                  Claim the owner account
                </button>
              </div>

              <button onClick={signOut} className="btn-subtle w-full text-2xs">
                Sign in as someone else
              </button>
            </div>
          ) : (
            <>
              {invitedEmail && (
                <div className="mb-4">
                  <Banner tone="info">
                    You've been invited. Create your account with <strong>{invitedEmail}</strong>{' '}
                    — or sign in, if you already have a Coquis account with that address.
                  </Banner>
                </div>
              )}

              <div className="mb-4 flex rounded-lg border border-hairline p-0.5">
                {[
                  ['signin', 'Sign in'],
                  ['signup', 'Register'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => {
                      setMode(value)
                      setError(null)
                    }}
                    className={`flex-1 rounded-md px-3 py-1.5 text-2xs font-medium transition-colors ${
                      mode === value ? 'bg-brand-navy text-white' : 'text-ink-secondary hover:text-ink'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label className="label" htmlFor="name">
                      Your name
                    </label>
                    <input
                      id="name"
                      className="input"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      autoComplete="name"
                    />
                  </div>
                )}

                <div>
                  <label className="label" htmlFor="email">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoFocus
                  />
                  {mode === 'signup' && (
                    <p className="field-hint">
                      Must be the address you were invited on, or you will have no access.
                    </p>
                  )}
                </div>

                <div>
                  <label className="label" htmlFor="password">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    minLength={mode === 'signup' ? 8 : undefined}
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  />
                  {mode === 'signup' && <p className="field-hint">At least 8 characters.</p>}
                </div>

                {notice && <Banner tone="success">{notice}</Banner>}
                {(error || authError) && <Banner tone="error">{error ?? authError}</Banner>}

                <button type="submit" disabled={busy} className="btn-primary w-full">
                  {busy ? (
                    <FaSpinner className="animate-spin" />
                  ) : mode === 'signup' ? (
                    <FaUserPlus />
                  ) : (
                    <FaLock />
                  )}
                  {mode === 'signup' ? 'Create my account' : 'Sign in'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-2xs leading-relaxed text-white/40">
          Registering does not grant access on its own — the owner and managers decide who gets
          in, and what they may do, from Team.
        </p>
      </div>
    </div>
  )
}

export default LoginPage
